import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import { Select, TextInput, Column, Row } from '@pella/sash-framework';
import { qasReset, qasSearchv2, qasFormatv2 } from '../../../../actions/qas-actions';
import { getCountryFromPostal, htmlDecode } from '../../../utilities/functions';
import { isValidPostal } from '../../../utilities/validations';
import { poBoxRegex } from '../../../utilities/constants';
import StatesOptions from '../../states-options';
import styles from './validation.module.scss';

const AddressValidation = (props) => {
	const { errors: errorContent, form_labels, validation_responses } = useStaticQuery(query)?.contentstackAddressValidation;
	const dispatch = useDispatch();
	const qasData = useSelector((state) => state.qasData);

	const [addressComplete, setAddressComplete] = props.addressComplete;
	const [addressOutput, setAddressOutput] = props.addressOutput;
	const [addressModified, setAddressModified] = props.addressModified;

	const [address, setAddress] = useState({
		address1: props.addressInput?.line_1 || '',
		address2: props.addressInput?.line_2 || '',
		city: props.addressInput?.city || '',
		state: props.addressInput?.state || '',
		postalCode: props.addressInput?.postcode || '',
		country: props.addressInput?.country || '',
		addressValidated: props.addressInput && Object.keys(props.addressInput).length > 0 ? true : false,
	});

	const [errors, setErrors] = useState({ address1: '', address2: '', city: '', state: '', postalCode: '', country: '' });
	const [addressListOpen, setAddressListOpen] = useState(false);

	useEffect(() => {
		document.addEventListener('click', onOutsideAddressListClick);
		return () => document.removeEventListener('click', onOutsideAddressListClick);
	}, []);

	useEffect(() => {
		// Watch for any update to address object
		if (!address.address1 || !address.city || !address.state || !address.postalCode || !address.country || (!props.poBoxAllowed && address.address1.match(poBoxRegex))) {
			setAddressComplete(false);
		} else {
			setErrors({
				address1: '',
				address2: '',
				city: '',
				state: '',
				postalCode: '',
				country: '',
			});
			if (isValidPostal(address.postalCode)) setAddressComplete(true);
			setAddressOutput(address);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- on checkout having setAddressComplete & setAddressOutput as dependencies causes infinite loop, attempted to fix but need to return to this at a later date
	}, [address]);
	// }, [address, setAddressComplete, setAddressOutput]);

	useEffect(() => {
		// Watch for responses from QAS address validation
		if (qasData?.result?.address) {
			const newAddress = {
				address1: qasData?.result?.address?.address_line_1,
				address2: qasData?.result?.address?.address_line_2,
				city: qasData?.result?.address.locality,
				state: qasData?.result?.address?.region,
				postalCode: qasData?.result?.address?.postal_code,
				// QAS returns country-state, need just US, may need to update to save both ISO1 & ISO2 country for various use cases
				country: qasData?.result?.components?.country_iso_2?.substring(0, 2),
				addressValidated: true,
			};

			dispatch(qasReset());
			if (address.address1 !== newAddress.address1 || address.address2 !== newAddress.address2 || address.city !== newAddress.city || address.state !== newAddress.state || address.postalCode !== newAddress.postalCode || address.country !== newAddress.country) {
				setAddress({ ...address, ...newAddress });
			}
		}
	}, [address, dispatch, qasData.result]);

	useEffect(() => {
		// Watch for postal changes to update country
		if (isValidPostal(address.postalCode)) {
			const country = getCountryFromPostal(address.postalCode, 5);
			setAddress({ ...address, country: country });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [address.postalCode]);

	useEffect(() => {
		// Trigger a validation to run for page level erroring (keeping as props saves it from failing)
		if (props.addressValidate?.[0]) {
			props.addressValidate[1](false);

			let errors = {};
			Object.entries(address).forEach((item) => {
				const value = item[1] ? item[1] : '';
				let message = validateField(item[0], value);
				if (message || errors[item[0]] !== message) {
					errors[item[0]] = message;
				}
			});
			setErrors(errors);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [props.addressValidate]);

	useEffect(() => {
		if (props.usePreviousAddress?.[0]) {
			setAddress({
				address1: props.addressInput.line_1,
				address2: props.addressInput.line_2,
				city: props.addressInput.city,
				state: props.addressInput.state,
				postalCode: props.addressInput.postcode,
				country: props.addressInput.country,
				addressValidated: true,
			});
			props.usePreviousAddress[1](false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [props.usePreviousAddress]);

	function onOutsideAddressListClick(e) {
		if (e.target.className !== 'js-AddressOption') setAddressListOpen(false);
	}

	function queryAddress(e) {
		addressChange(e);
		setAddressListOpen(true);
		const value = e.target.value;
		setAddress({
			...address,
			addressValidated: false,
			address1: value,
			address2: '',
			city: '',
			state: '',
			postalCode: '',
			country: '',
		});
		if (value) {
			let country = 'USA';
			if (!props.defaultedPostalCode && !address.postalCode && navigator.language === 'en-CA') {
				country = 'CAN';
			} else if (props.defaultedPostalCode) {
				country = getCountryFromPostal(props.defaultedPostalCode, 5);
			} else if (address.postalCode) {
				country = getCountryFromPostal(address.postalCode, 5);
			}
			dispatch(qasSearchv2(value, country));
			setErrors({
				...errors,
				address1: validateField('address1', value),
			});
		} else {
			dispatch(qasReset());
			setErrors({
				...errors,
				address1: validateField('address1', value),
			});
		}
	}

	function addressChange(e) {
		setAddressComplete(false);
		setAddressModified(true);
		const field = e.target.name;
		const value = e.target.value;
		const addressObject = JSON.parse(JSON.stringify(address));
		addressObject[field] = value;
		const newAddress = {
			[field]: value,
			country: getCountryFromPostal(address.postalCode),
		};
		setAddress({ ...address, ...newAddress });
		setErrors({ ...errors, [field]: validateField(field, value) });
		setAddressOutput({ ...address, ...newAddress }); // added to provide page-level error handling.
	}

	function onInputFocus() {
		setAddressListOpen(false);
	}

	function addressBlur(e) {
		const field = e.target.name;
		const value = e.target.value;
		let message = '';
		if (value === '') {
			message = validateField(field, value);
		} else if (field === 'postalCode') {
			message = !isValidPostal(value) ? errorContent.find(({ field_id }) => field_id === 'postal_code').error_2 : '';
		} else if (!props.poBoxAllowed && field === 'address1') {
			message = value.match(poBoxRegex) ? errorContent.find(({ field_id }) => field_id === 'po_box').error_2 : '';
		}

		if (message || errors[field] !== message) {
			setErrors({ ...errors, [field]: message });
		}
	}

	function validateField(name, value) {
		if (props.noValidation) return '';
		let message;
		switch (name) {
			case 'address1':
				if (value === '') {
					message = errorContent.find(({ field_id }) => field_id === 'address1').error_2;
				} else if (!props.poBoxAllowed && value.match(poBoxRegex)) {
					message = errorContent.find(({ field_id }) => field_id === 'po_box').error_2;
				} else {
					message = '';
				}
				break;
			case 'city':
				message = value === '' ? errorContent.find(({ field_id }) => field_id === 'city').error_2 : '';
				break;
			case 'state':
				message = value === '' ? errorContent.find(({ field_id }) => field_id === 'state').error_2 : '';
				break;
			case 'postalCode':
				if (value === '' || !isValidPostal(value)) {
					message = errorContent.find(({ field_id }) => field_id === 'postal_code').error_2;
				} else {
					message = '';
				}
				break;
			default:
				break;
		}
		return message;
	}

	function formatAddress(addressKey) {
		dispatch(qasFormatv2(addressKey));
		setAddress({ ...address, address1: qasData.result.address || '' }); // addeded '' to fix address changing to uncontrolled input
		setErrors({
			...errors,
			address1: '',
			address2: '',
			city: '',
			state: '',
			postalCode: '',
			country: '',
		});
	}

	return (
		<Row
			className={`${styles.addressValidation} ${props.className || ''}`}
			data-testid='address-validation-v3'
		>
			<Column
				xs={props.addressInputs?.line1?.xs}
				sm={props.addressInputs?.line1?.sm}
				md={props.addressInputs?.line1?.md}
				lg={props.addressInputs?.line1?.lg}
				xl={props.addressInputs?.line1?.xl}
			>
				<TextInput
					id='address1'
					className='rtaForm'
					labelText={props.addressInputs?.line1?.label ?? form_labels.address}
					value={address.address1}
					onChange={queryAddress}
					onBlur={addressBlur}
					required={props.noValidation ? false : true}
					error={errors.address1 ? true : false}
					errorText={props.addressInputs?.line1?.error || errors.address1}
					assistiveText={props.addressInputs?.line1?.assistiveText}
					autoComplete='address-line1'
				/>

				{address.address1 && !address.addressValidated && addressListOpen && (
					<ul
						role='menu'
						className={styles.addressList}
					>
						{qasData?.result?.suggestions === undefined && (
							<li
								role='presentation'
								value={`${validation_responses.trying}${htmlDecode('&hellip;')}`}
							>
								{`${validation_responses.trying}${htmlDecode('&hellip;')}`}
							</li>
						)}
						{qasData?.result?.suggestions !== undefined && qasData?.result?.suggestions.length === 0 && (
							<li
								role='presentation'
								value={validation_responses.no_match}
							>
								{validation_responses.no_match}
							</li>
						)}
						{qasData?.result?.suggestions !== undefined &&
							qasData?.result?.suggestions?.length > 0 &&
							!address.addressValidated &&
							address.address1 &&
							qasData?.result?.suggestions.map((item, index) => (
								<li
									role='presentation'
									key={`a${index}`}
									value={item.text}
									className={'js-AddressOption'}
									onClick={() => {
										formatAddress(item.global_address_key);
									}}
								>
									{item.text}
								</li>
							))}
					</ul>
				)}
			</Column>

			{props.addressInputs?.line2?.visible && (
				<Column
					xs={props.addressInputs?.line2?.xs}
					sm={props.addressInputs?.line2?.sm}
					md={props.addressInputs?.line2?.md}
					lg={props.addressInputs?.line2?.lg}
					xl={props.addressInputs?.line2?.xl}
				>
					<TextInput
						id='address2'
						labelText={props.addressInputs?.line2.label ?? form_labels.address_line_2}
						value={address.address2}
						onChange={addressChange}
						autoComplete='address-line2'
					/>
				</Column>
			)}

			<Column
				xs={props.addressInputs?.city?.xs}
				sm={props.addressInputs?.city?.sm}
				md={props.addressInputs?.city?.md}
				lg={props.addressInputs?.city?.lg}
				xl={props.addressInputs?.city?.xl}
			>
				<TextInput
					id='city'
					labelText={props.addressInputs?.city?.label ?? form_labels.city}
					value={address.city}
					onChange={addressChange}
					onBlur={addressBlur}
					onFocus={onInputFocus}
					required={props.noValidation ? false : true}
					error={errors.city ? true : false}
					errorText={props.addressInputs?.city?.error || errors.city}
					autoComplete='address-level2'
				/>
			</Column>

			<Column
				xs={props.addressInputs?.state?.xs}
				sm={props.addressInputs?.state?.sm}
				md={props.addressInputs?.state?.md}
				lg={props.addressInputs?.state?.lg}
				xl={props.addressInputs?.state?.xl}
			>
				<Select
					id='state'
					labelText={props.addressInputs?.state?.label ?? (props.allowInternational ? (address.postalCode.search(/\D\d\D/i) > -1 ? form_labels.province : form_labels.state) : form_labels.state)}
					value={address.state}
					onChange={addressChange}
					onBlur={addressBlur}
					onFocus={onInputFocus}
					required={props.noValidation ? false : true}
					error={errors.state ? true : false}
					errorText={props.addressInputs?.state?.error || errors.state}
				>
					<option value=''>{form_labels.select}&hellip;</option>
					<StatesOptions allowInternational={props.allowInternational} />
				</Select>
			</Column>

			<Column
				xs={props.addressInputs?.postalCode?.xs}
				sm={props.addressInputs?.postalCode?.sm}
				md={props.addressInputs?.postalCode?.md}
				lg={props.addressInputs?.postalCode?.lg}
				xl={props.addressInputs?.postalCode?.xl}
			>
				<TextInput
					id='postalCode'
					labelText={props.addressInputs?.postalCode?.label ?? form_labels.postal_code}
					value={address.postalCode}
					onChange={addressChange}
					onBlur={addressBlur}
					onFocus={onInputFocus}
					required={props.noValidation ? false : true}
					error={errors.postalCode ? true : false}
					errorText={props.addressInputs?.postalCode?.error || errors.postalCode}
					autoComplete='postal-code'
				/>
			</Column>
		</Row>
	);
};

AddressValidation.propTypes = {
	addressInputs: PropTypes.object,
	addressInput: PropTypes.object,
	defaultedPostalCode: PropTypes.string,
	allowInternational: PropTypes.bool,
	addressOutput: PropTypes.array,
	addressComplete: PropTypes.array,
	addressModified: PropTypes.array,
	addressValidate: PropTypes.array,
	usePreviousAddress: PropTypes.array,
	className: PropTypes.string,
	noValidation: PropTypes.bool,
	poBoxAllowed: PropTypes.bool,
};

export default AddressValidation;

export const query = graphql`
	query {
		contentstackAddressValidation {
			errors {
				error
				error_2
				field_id
			}
			form_labels {
				address
				address_line_2
				city
				postal_code
				province
				select
				state
			}
			validation_responses {
				no_match
				trying
			}
		}
	}
`;
